import React, { useEffect, useState } from 'react';
import {
  Dumbbell, Plus, Search, CalendarCheck, RefreshCw, Ban, Trash2,
  CheckCircle2, Clock, AlertTriangle, XCircle, LogIn
} from 'lucide-react';
import { membershipsApi, Membership, clientsApi } from '../services/api';
import toast from 'react-hot-toast';

type StatusFilter = 'all' | 'active' | 'expiring' | 'expired' | 'cancelled';
type TabKey = 'memberships' | 'presences';

const statusMeta: Record<string, { label: string; cls: string }> = {
  active: { label: 'Ativo', cls: 'bg-green-100 text-green-800' },
  expiring: { label: 'A expirar', cls: 'bg-amber-100 text-amber-800' },
  expired: { label: 'Expirado', cls: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Cancelado', cls: 'bg-gray-100 text-gray-800' },
};

const PLAN_PRESETS = [
  { label: 'Passe de 1 Dia (Passe Livre)', days: 1 },
  { label: 'Semanal', days: 7 },
  { label: 'Mensal', days: 30 },
  { label: 'Quinzenal', days: 15 },
  { label: 'Trimestral', days: 90 },
  { label: 'Semestral', days: 180 },
  { label: 'Anual', days: 365 },
];

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' });

const formatPrice = (v: number) => `${v.toLocaleString()} MT`;

export const Memberships: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('memberships');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [presences, setPresences] = useState<{ membership: Membership; checkIn: any }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [renewing, setRenewing] = useState<Membership | null>(null);
  const [checkingInId, setCheckingInId] = useState<string | null>(null);

  const loadMemberships = async () => {
    try {
      const [list, sum] = await Promise.all([
        membershipsApi.getAll({ status: statusFilter, search: search || undefined }),
        membershipsApi.getSummary(),
      ]);
      setMemberships(list);
      setSummary(sum);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao carregar membresias');
    } finally {
      setLoading(false);
    }
  };

  const loadPresences = async () => {
    try {
      const today = new Date().toISOString();
      const list = await membershipsApi.getPresences(today);
      setPresences(list);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao carregar presenças');
    }
  };

  useEffect(() => {
    loadMemberships();
  }, [statusFilter, search]);

  useEffect(() => {
    if (tab === 'presences') loadPresences();
  }, [tab]);

  const handleCheckIn = async (id: string) => {
    setCheckingInId(id);
    try {
      await membershipsApi.checkIn(id);
      toast.success('Check-in registado ✓');
      await Promise.all([loadMemberships(), loadPresences()]);
    } catch (err: any) {
      toast.error(err?.error || err.message || 'Não foi possível registar o check-in');
    } finally {
      setCheckingInId(null);
    }
  };

  const handleCancel = async (m: Membership) => {
    if (!window.confirm(`Cancelar a membresia de ${getMemberName(m)}?`)) return;
    try {
      await membershipsApi.cancel(m._id);
      toast.success('Membresia cancelada');
      loadMemberships();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao cancelar');
    }
  };

  const handleDelete = async (m: Membership) => {
    if (!window.confirm(`Eliminar definitivamente a membresia de ${getMemberName(m)}?`)) return;
    try {
      await membershipsApi.remove(m._id);
      toast.success('Membresia eliminada');
      loadMemberships();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao eliminar');
    }
  };

  const getMemberName = (m: Membership) =>
    typeof m.client === 'object' ? m.client?.name || 'Cliente' : 'Cliente';

  const statusBadge = (m: Membership) => {
    const meta = statusMeta[m.status] || statusMeta.expired;
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${meta.cls}`}>
        {meta.label}
      </span>
    );
  };

  const statCard = (icon: React.ReactNode, value: number, label: string, color: string) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>{icon}</div>
      <div>
        <p className="text-2xl font-black text-gray-900 leading-none">{value}</p>
        <p className="text-xs text-gray-500 mt-1 font-medium">{label}</p>
      </div>
    </div>
  );

  if (loading && memberships.length === 0) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter flex items-center gap-3">
            <Dumbbell className="text-blue-600" size={28} />
            Ginásio — Membresias
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Planos, validade automática e presenças dos membros
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center justify-center gap-2 font-bold shadow-lg transition-all"
        >
          <Plus size={20} />
          Nova Membresia
        </button>
      </div>

      {/* Resumo */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {statCard(<CheckCircle2 className="text-green-600" size={20} />, summary.active, 'Ativas', 'bg-green-50')}
          {statCard(<Clock className="text-amber-600" size={20} />, summary.expiring, 'A expirar', 'bg-amber-50')}
          {statCard(<AlertTriangle className="text-red-600" size={20} />, summary.expired, 'Expiradas', 'bg-red-50')}
          {statCard(<Ban className="text-gray-500" size={20} />, summary.cancelled, 'Canceladas', 'bg-gray-50')}
          {statCard(<CalendarCheck className="text-indigo-600" size={20} />, summary.todayCheckIns, 'Presenças hoje', 'bg-indigo-50')}
          {statCard(<RefreshCw className="text-blue-600" size={20} />, summary.todayExpiring, 'Vencem hoje', 'bg-blue-50')}
        </div>
      )}

      {/* Abas */}
      <div className="flex space-x-2">
        {([
          ['memberships', 'Membresias'],
          ['presences', 'Presenças de Hoje'],
        ] as [TabKey, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              tab === key ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {label}
            {key === 'presences' && summary?.todayCheckIns ? ` (${summary.todayCheckIns})` : ''}
          </button>
        ))}
      </div>

      {tab === 'memberships' ? (
        <>
          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Procurar por nome ou telefone..."
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="border border-gray-300 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
            >
              {(['all', 'active', 'expiring', 'expired', 'cancelled'] as StatusFilter[]).map((s) => (
                <option key={s} value={s}>{s === 'all' ? 'Todos os estados' : statusMeta[s].label}</option>
              ))}
            </select>
          </div>

          {/* Lista */}
          {memberships.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-10 text-center">
              <Dumbbell className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Nenhuma membresia encontrada</h3>
              <p className="text-gray-500 mb-6">
                Registe a primeira membresia para começar a controlar planos e validade.
              </p>
              <button
                onClick={() => setShowNew(true)}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-700 transition"
              >
                <Plus size={18} /> Nova Membresia
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {memberships.map((m) => (
                <div key={m._id} className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg transition-all overflow-hidden">
                  <div className="p-5 border-b border-gray-100 bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg">{getMemberName(m)}</h3>
                        <p className="text-sm text-gray-500">
                          {typeof m.client === 'object' && (m.client.phone || m.client.email) ? (m.client.phone || m.client.email) : '—'}
                        </p>
                      </div>
                      {statusBadge(m)}
                    </div>
                  </div>

                  <div className="p-5 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 font-medium">{m.plan}</span>
                      <span className="font-bold text-xl text-blue-700">{formatPrice(m.price)}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Início</p>
                        <p className="font-medium">{formatDate(m.startDate)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Expira</p>
                        <p className={`font-medium ${m.status === 'expired' ? 'text-red-600' : m.status === 'expiring' ? 'text-amber-600' : ''}`}>
                          {formatDate(m.expiryDate)}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between text-sm pt-3 border-t border-gray-100">
                      <span className="text-gray-600">Pagamento</span>
                      <span className="font-medium">{m.paymentMethod}</span>
                    </div>

                    {m.status === 'active' || m.status === 'expiring' ? (
                      <button
                        onClick={() => handleCheckIn(m._id)}
                        disabled={checkingInId === m._id || m.checkedInToday}
                        className={`w-full py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 transition ${
                          m.checkedInToday
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        } disabled:opacity-70`}
                      >
                        {checkingInId === m._id ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : m.checkedInToday ? (
                          <>
                            <CheckCircle2 size={16} /> Check-in feito hoje
                          </>
                        ) : (
                          <>
                            <LogIn size={16} /> Registar check-in
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <button
                          onClick={() => setRenewing(m)}
                          className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition"
                        >
                          <RefreshCw size={16} className="inline mr-1" /> Renovar
                        </button>
                        <button
                          onClick={() => handleCancel(m)}
                          className="p-2.5 text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50"
                          title="Cancelar membresia"
                        >
                          <Ban size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(m)}
                          className="p-2.5 text-red-500 border border-gray-200 rounded-xl hover:bg-red-50"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}

                    {m.status === 'cancelled' && (
                      <div className="flex items-center justify-center gap-2">
                        <Ban size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-500">Membresia cancelada</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <CalendarCheck size={16} className="text-indigo-600" />
            Presenças registadas hoje — {presences.length} membros
          </div>
          {presences.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-10 text-center">
              <CalendarCheck className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Sem presenças hoje</h3>
              <p className="text-gray-500 mb-6">
                Registre o check-in dos membros a partir da aba Membresias.
              </p>
              <button
                onClick={() => setTab('memberships')}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-700 transition"
              >
                Ver membros
              </button>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Membro</th>
                    <th className="text-left px-5 py-3 font-semibold">Plano</th>
                    <th className="text-left px-5 py-3 font-semibold">Expira</th>
                    <th className="text-left px-5 py-3 font-semibold">Hora</th>
                    <th className="text-left px-5 py-3 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {presences.map(({ membership: m, checkIn }) => (
                    <tr key={m._id} className="hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <p className="font-medium text-gray-900">{getMemberName(m)}</p>
                        {typeof m.client === 'object' && (m.client.phone || m.client.email) && (
                          <p className="text-xs text-gray-500">{m.client.phone || m.client.email}</p>
                        )}
                      </td>
                      <td className="px-5 py-3">{m.plan}</td>
                      <td className="px-5 py-3">{formatDate(m.expiryDate)}</td>
                      <td className="px-5 py-3">{checkIn ? new Date(checkIn.date).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td className="px-5 py-3">{statusBadge(m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showNew && <NewMembershipModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); loadMemberships(); }} />}
      {renewing && <RenewModal membership={renewing} onClose={() => setRenewing(null)} onRenewed={() => { setRenewing(null); loadMemberships(); }} />}
    </div>
  );
};

// ────────────────────────────────────────────────────
// Modal de criação
// ────────────────────────────────────────────────────
const NewMembershipModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const [clients, setClients] = useState<Array<{ _id: string; name: string; phone?: string }>>([]);
  const [form, setForm] = useState({
    client: '',
    plan: 'Mensal',
    durationDays: 30,
    price: 0,
    paymentMethod: 'Cash',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    clientsApi
      .getAll({ limit: 200 })
      .then((res) => { if (active) setClients(res?.clients || []); })
      .catch(() => toast.error('Erro ao carregar clientes'));
    return () => { active = false; };
  }, []);

  const pickPlan = (days: number) => {
    const preset = PLAN_PRESETS.find((p) => p.days === days);
    setForm({ ...form, durationDays: days, plan: preset ? preset.label.split(' (')[0] : String(days) + ' dias' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.client) return toast.error('Selecione o membro');
    if (form.durationDays < 1) return toast.error('Duração inválida');
    setSaving(true);
    try {
      await membershipsApi.create(form);
      toast.success('Membresia registada com sucesso');
      onCreated();
    } catch (err: any) {
      toast.error(err?.error || err.message || 'Erro ao registar membresia');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter flex items-center gap-3">
              <Dumbbell className="text-blue-600" size={28} /> Nova Membresia
            </h2>
            <p className="text-sm text-gray-500 mt-1">Registe um membro e o seu plano de acesso</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
            <XCircle size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Membro (cliente) <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              value={form.client}
              onChange={(e) => setForm({ ...form, client: e.target.value })}
              required
            >
              <option value="">Selecione o membro...</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>{c.name}{c.phone ? ` • ${c.phone}` : ''}</option>
              ))}
            </select>
            <p className="text-xs text-blue-700 mt-2">
              Membro ainda não registado? Crie o cliente na secção Clientes antes de registar a membresia.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Plano / Validade</label>
            <div className="flex flex-wrap gap-2 mb-4">
              {PLAN_PRESETS.map((p) => (
                <button
                  key={p.days}
                  type="button"
                  onClick={() => pickPlan(p.days)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                    form.durationDays === p.days
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do plano</label>
                <input
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  value={form.plan}
                  onChange={(e) => setForm({ ...form, plan: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duração (dias)</label>
                <input
                  type="number"
                  min={1}
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  value={form.durationDays}
                  onChange={(e) => setForm({ ...form, durationDays: Number(e.target.value) })}
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preço pago (MT)</label>
              <input
                type="number"
                min={0}
                step="0.01"
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Método de pagamento</label>
              <select
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                value={form.paymentMethod}
                onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
              >
                <option>Cash</option>
                <option>M-Pesa</option>
                <option>E-Mola</option>
                <option>POS</option>
                <option>Visa</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              rows={2}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
              placeholder="Observações opcionais..."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-8 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> A registar...</>
              ) : (
                <><CheckCircle2 size={18} /> Registar Membresia</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────
// Modal de renovação
// ────────────────────────────────────────────────────
const RenewModal: React.FC<{ membership: Membership; onClose: () => void; onRenewed: () => void }> = ({ membership, onClose, onRenewed }) => {
  const [form, setForm] = useState({
    plan: membership.plan,
    durationDays: membership.durationDays,
    price: membership.price,
    paymentMethod: membership.paymentMethod,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await membershipsApi.renew(membership._id, form);
      toast.success('Membresia renovada com sucesso');
      onRenewed();
    } catch (err: any) {
      toast.error(err?.error || err.message || 'Erro ao renovar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-black text-gray-900 uppercase tracking-tighter flex items-center gap-2">
              <RefreshCw className="text-blue-600" size={22} /> Renovar Membresia
            </h2>
            <p className="text-sm text-gray-500 mt-1">Novo período começa hoje</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
            <XCircle size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plano</label>
              <input
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                value={form.plan}
                onChange={(e) => setForm({ ...form, plan: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duração (dias)</label>
              <input
                type="number"
                min={1}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                value={form.durationDays}
                onChange={(e) => setForm({ ...form, durationDays: Number(e.target.value) })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preço (MT)</label>
              <input
                type="number"
                min={0}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Método de pagamento</label>
              <select
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                value={form.paymentMethod}
                onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
              >
                <option>Cash</option>
                <option>M-Pesa</option>
                <option>E-Mola</option>
                <option>POS</option>
                <option>Visa</option>
              </select>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2">
            <Clock size={16} className="mt-0.5 shrink-0" />
            A renovação reinicia a validade a partir de hoje. A membresia fica novamente Ativa.
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> A renovar...</>
              ) : (
                <><RefreshCw size={18} /> Renovar Agora</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};